import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

interface UICtx {
  isNavbarVisible: boolean;
  showNavbar: () => void;
  hideNavbar: () => void;
  triggerNavbarAutoHide: () => void;
}

const UIContext = createContext<UICtx | null>(null);

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) throw new Error('useUI must be used within a UIProvider');
  return context;
};

export const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const showNavbar = useCallback(() => {
    clearTimer();
    setIsNavbarVisible(true);
  }, [clearTimer]);

  const hideNavbar = useCallback(() => {
    clearTimer();
    setIsNavbarVisible(false);
  }, [clearTimer]);

  const triggerNavbarAutoHide = useCallback(() => {
    showNavbar();
    hideTimerRef.current = setTimeout(() => {
      setIsNavbarVisible(false);
      hideTimerRef.current = null;
    }, 3000);
  }, [showNavbar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return (
    <UIContext.Provider value={{ isNavbarVisible, showNavbar, hideNavbar, triggerNavbarAutoHide }}>
      {children}
    </UIContext.Provider>
  );
};
